import {
  discoveryApiRef,
  fetchApiRef,
  microsoftAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { useMemo } from 'react';

import type {
  BaoAuthMount,
  BaoMount,
  BaoPolicy,
  BaoSecret,
  BaoSession,
  BaoStatus,
} from './types';

interface MicrosoftIdTokenApi {
  getIdToken(): Promise<string>;
}

export class OpenBaoApi {
  constructor(
    private readonly discoveryApi: {
      getBaseUrl(pluginId: string): Promise<string>;
    },
    private readonly fetchApi: {
      fetch(input: RequestInfo, init?: RequestInit): Promise<Response>;
    },
    private readonly microsoftAuthApi: MicrosoftIdTokenApi,
  ) {}

  async status(): Promise<BaoStatus> {
    const response = await this.request<{ status: BaoStatus }>(
      '/native/status',
    );
    return response.status;
  }

  async session(namespace: string): Promise<BaoSession> {
    const response = await this.request<{ session: BaoSession }>(
      `/native/session${this.namespaceQuery(namespace)}`,
    );
    return response.session;
  }

  async renewSession(namespace: string): Promise<void> {
    await this.request(
      `/native/session/renew${this.namespaceQuery(namespace)}`,
      { method: 'POST' },
    );
  }

  async namespaces(): Promise<string[]> {
    const response = await this.request<{ namespaces: string[] }>(
      '/native/namespaces',
    );
    return response.namespaces;
  }

  async mounts(namespace: string): Promise<BaoMount[]> {
    const response = await this.request<{ mounts: BaoMount[] }>(
      `/native/mounts${this.namespaceQuery(namespace)}`,
    );
    return response.mounts;
  }

  async list(
    namespace: string,
    mount: string,
    path: string,
  ): Promise<string[]> {
    const query = this.query(namespace, { mount, path });
    const response = await this.request<{ keys: string[] }>(
      `/native/kv/list?${query}`,
    );
    return response.keys;
  }

  async secret(
    namespace: string,
    mount: string,
    path: string,
    version?: number,
  ): Promise<BaoSecret> {
    const query = this.query(namespace, {
      mount,
      path,
      ...(version ? { version: String(version) } : {}),
    });
    const response = await this.request<{ secret: BaoSecret }>(
      `/native/kv/secret?${query}`,
    );
    return response.secret;
  }

  async writeSecret(
    namespace: string,
    mount: string,
    path: string,
    data: Record<string, string>,
    cas?: number,
  ): Promise<void> {
    await this.request(
      `/native/kv/secret${this.namespaceQuery(namespace)}`,
      {
        method: 'POST',
        body: JSON.stringify({ mount, path, data, cas }),
      },
    );
  }

  async versionOperation(
    namespace: string,
    operation: 'delete' | 'undelete' | 'destroy',
    mount: string,
    path: string,
    versions?: number[],
  ): Promise<void> {
    await this.request(
      `/native/kv/${operation}${this.namespaceQuery(namespace)}`,
      {
        method: 'POST',
        body: JSON.stringify({ mount, path, versions }),
      },
    );
  }

  async deleteMetadata(
    namespace: string,
    mount: string,
    path: string,
  ): Promise<void> {
    const query = this.query(namespace, { mount, path });
    await this.request(`/native/kv/metadata?${query}`, { method: 'DELETE' });
  }

  async policies(namespace: string): Promise<string[]> {
    const response = await this.request<{ policies: string[] }>(
      `/native/policies${this.namespaceQuery(namespace)}`,
    );
    return response.policies;
  }

  async policy(namespace: string, name: string): Promise<BaoPolicy> {
    const response = await this.request<{ policy: BaoPolicy }>(
      `/native/policies/${encodeURIComponent(name)}${this.namespaceQuery(
        namespace,
      )}`,
    );
    return response.policy;
  }

  async writePolicy(
    namespace: string,
    name: string,
    policy: string,
  ): Promise<void> {
    await this.request(
      `/native/policies/${encodeURIComponent(name)}${this.namespaceQuery(
        namespace,
      )}`,
      { method: 'PUT', body: JSON.stringify({ policy }) },
    );
  }

  async deletePolicy(namespace: string, name: string): Promise<void> {
    await this.request(
      `/native/policies/${encodeURIComponent(name)}${this.namespaceQuery(
        namespace,
      )}`,
      { method: 'DELETE' },
    );
  }

  async authMounts(namespace: string): Promise<BaoAuthMount[]> {
    const response = await this.request<{ authMounts: BaoAuthMount[] }>(
      `/native/auth-mounts${this.namespaceQuery(namespace)}`,
    );
    return response.authMounts;
  }

  private namespaceQuery(namespace: string): string {
    return namespace
      ? `?namespace=${encodeURIComponent(namespace)}`
      : '';
  }

  private query(
    namespace: string,
    params: Record<string, string>,
  ): URLSearchParams {
    const query = new URLSearchParams(params);
    if (namespace) query.set('namespace', namespace);
    return query;
  }

  private async request<T = unknown>(
    path: string,
    init: RequestInit = {},
  ): Promise<T> {
    const [baseUrl, idToken] = await Promise.all([
      this.discoveryApi.getBaseUrl('openbao'),
      this.microsoftAuthApi.getIdToken(),
    ]);
    if (!idToken) throw new Error('Microsoft Entra login is required');

    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, {
      ...init,
      headers: {
        'X-Bao-Authorization': `Bearer ${idToken}`,
        ...(init.body ? { 'Content-Type': 'application/json' } : {}),
        ...init.headers,
      },
    });

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(
        typeof body.error === 'string'
          ? body.error
          : `OpenBao request failed (${response.status})`,
      );
    }
    if (response.status === 204) return undefined as T;
    return response.json() as Promise<T>;
  }
}

export function useOpenBaoApi(): OpenBaoApi {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const microsoftAuthApi = useApi(microsoftAuthApiRef);

  return useMemo(
    () => new OpenBaoApi(discoveryApi, fetchApi, microsoftAuthApi),
    [discoveryApi, fetchApi, microsoftAuthApi],
  );
}
